import { useState } from "react";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { formatDatePretty, formatRelativeTime } from "../../lib/progress";
import { STATUS_STYLES } from "./constants";

const STATUS_OPTIONS = ["Present", "Absent", "Excused", "Double Session"];

export function AttendanceHistoryRow({ record, isFlash, onEdit, onDelete, readOnly = false }) {
    const styles = STATUS_STYLES[record.status];
    const timestamp = record.updatedAt || record.createdAt;

    const [mode, setMode] = useState("view"); // "view" | "edit" | "confirmDelete"
    const [status, setStatus] = useState(record.status);
    const [remark, setRemark] = useState(record.remark || "");

    const startEdit = () => {
        setStatus(record.status);
        setRemark(record.remark || "");
        setMode("edit");
    };

    const cancel = () => setMode("view");

    const saveEdit = () => {
        if (!status) {
            toast.error("Pick a status");
            return;
        }
        const changed =
            status !== record.status || (remark || "") !== (record.remark || "");
        if (!changed) {
            setMode("view");
            return;
        }
        onEdit?.(record, { status, remark });
        setMode("view");
    };

    const confirmDelete = () => {
        onDelete?.(record);
        setMode("view");
    };

    return (
        <li
            className={`py-3 rounded-xl transition-colors -mx-2 px-2 ${
                isFlash
                    ? "bg-action-soft ring-2 ring-[#3D6385]/40 animate-saved"
                    : mode === "view"
                      ? "hover:bg-mist/50 group"
                      : "bg-mist/40"
            }`}
            data-testid={`history-row-${record.id}`}
        >
            <div className="flex items-start gap-3">
                <span
                    className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${styles?.dot || "bg-slate-300"}`}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display font-bold text-slate-900">
                            {formatDatePretty(record.date)}
                        </span>
                        <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${styles?.chip || "bg-mist text-slate-600"}`}
                        >
                            {record.status}
                        </span>
                        {timestamp && (
                            <span
                                className="text-[11px] text-slate-400 font-medium"
                                data-testid={`history-row-time-${record.id}`}
                            >
                                · logged {formatRelativeTime(timestamp)}
                            </span>
                        )}
                    </div>
                    {mode === "view" && record.remark && (
                        <div className="text-sm text-slate-500 mt-1">
                            {record.remark}
                        </div>
                    )}
                </div>

                {/* Row actions — hidden on archived / read-only rows */}
                {mode === "view" && !readOnly && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <button
                            type="button"
                            onClick={startEdit}
                            className="focus-ring w-8 h-8 inline-flex items-center justify-center rounded-lg text-ink-mute hover:text-action hover:bg-white"
                            data-testid={`history-edit-${record.id}`}
                            title="Edit"
                            aria-label="Edit record"
                        >
                            <Pencil size={14} strokeWidth={2.2} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("confirmDelete")}
                            className="focus-ring w-8 h-8 inline-flex items-center justify-center rounded-lg text-ink-mute hover:text-danger hover:bg-white"
                            data-testid={`history-delete-${record.id}`}
                            title="Delete"
                            aria-label="Delete record"
                        >
                            <Trash2 size={14} strokeWidth={2.2} />
                        </button>
                    </div>
                )}
            </div>

            {/* Edit form */}
            {mode === "edit" && (
                <div
                    className="mt-3 ml-6 space-y-3"
                    data-testid={`history-edit-form-${record.id}`}
                >
                    <div className="flex flex-wrap gap-1.5">
                        {STATUS_OPTIONS.map((s) => {
                            const active = s === status;
                            const sty = STATUS_STYLES[s];
                            return (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setStatus(s)}
                                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border transition-all ${
                                        active
                                            ? sty.active
                                            : `${sty.idle} bg-white`
                                    }`}
                                    data-testid={`history-edit-status-${record.id}-${s.replace(/\s+/g, "-")}`}
                                >
                                    {s}
                                </button>
                            );
                        })}
                    </div>
                    <textarea
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                        rows={2}
                        placeholder="Note (optional)…"
                        className="w-full text-sm rounded-lg border border-mist bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-action/40"
                        data-testid={`history-edit-remark-${record.id}`}
                    />
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={saveEdit}
                            className="focus-ring inline-flex items-center gap-1.5 rounded-lg bg-action hover:bg-action-hover text-white text-xs font-semibold h-8 px-3"
                            data-testid={`history-edit-save-${record.id}`}
                        >
                            <Check size={14} strokeWidth={2.6} />
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={cancel}
                            className="focus-ring inline-flex items-center gap-1.5 rounded-lg bg-white border border-mist text-ink-soft hover:text-ink text-xs font-semibold h-8 px-3"
                            data-testid={`history-edit-cancel-${record.id}`}
                        >
                            <X size={14} strokeWidth={2.6} />
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Delete confirm */}
            {mode === "confirmDelete" && (
                <div
                    className="mt-2 ml-6 flex flex-wrap items-center gap-2 text-sm"
                    data-testid={`history-delete-confirm-${record.id}`}
                >
                    <span className="text-ink-soft">Delete this record?</span>
                    <button
                        type="button"
                        onClick={confirmDelete}
                        className="focus-ring inline-flex items-center gap-1.5 rounded-lg bg-danger hover:bg-danger/90 text-white text-xs font-semibold h-8 px-3"
                        data-testid={`history-delete-confirm-yes-${record.id}`}
                    >
                        <Trash2 size={14} strokeWidth={2.6} />
                        Delete
                    </button>
                    <button
                        type="button"
                        onClick={cancel}
                        className="focus-ring inline-flex items-center gap-1.5 rounded-lg bg-white border border-mist text-ink-soft hover:text-ink text-xs font-semibold h-8 px-3"
                        data-testid={`history-delete-confirm-no-${record.id}`}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </li>
    );
}
